import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowUpRight, Calendar, Youtube } from "lucide-react";
import api from "@/lib/api";

const CHANNEL_URL = "https://www.youtube.com/@garlandebenezerghanaiansda";

const formatDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

/**
 * Homepage "Sermon of the Week" — auto-pulls the latest video from the church YouTube channel.
 * If embedding/loading fails, gracefully falls back to a clean featured card with a WATCH SERMONS CTA.
 */
export const SermonOfTheWeek = () => {
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [channelUrl, setChannelUrl] = useState(CHANNEL_URL);
  const [play, setPlay] = useState(false);

  useEffect(() => {
    api.get("/youtube/videos", { params: { limit: 1 } })
      .then((r) => {
        const v = (r.data.videos || [])[0];
        setVideo(v || null);
        if (r.data.channel_url) setChannelUrl(r.data.channel_url);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  // No video available — clean fallback CTA card (no error/placeholder text)
  if (!video) {
    return (
      <section data-testid="sermon-of-the-week-fallback" className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-5">
                Sermon of the Week
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#003B5C] leading-tight mb-6">
                Watch our latest message.
              </h2>
              <p className="text-base sm:text-lg leading-relaxed text-[#1E1E1E]/75 mb-8 max-w-2xl">
                Catch this week's sermon, recent worship messages, and livestream recordings on our
                official YouTube channel — new videos posted every Sabbath.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={channelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sermon-week-watch-sermons"
                  className="inline-flex items-center gap-2 bg-[#003B5C] text-white px-8 py-4 rounded-sm text-xs font-bold tracking-[0.18em] uppercase hover:bg-[#002A42] transition-all group"
                >
                  <Youtube size={16} className="text-[#D4A437]" />
                  Watch Sermons
                  <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
                <Link
                  to="/sermons"
                  data-testid="sermon-week-archive"
                  className="inline-flex items-center gap-2 border-2 border-[#003B5C] text-[#003B5C] px-8 py-4 rounded-sm text-xs font-bold tracking-[0.18em] uppercase hover:bg-[#003B5C] hover:text-white transition-all"
                >
                  Sermon Archive
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-2">
              <div className="aspect-video bg-[#003B5C] rounded-2xl overflow-hidden relative grain-overlay">
                <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#D4A437]/15" />
                <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-[#D4A437]/10" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-[#D4A437] flex items-center justify-center">
                    <Play size={32} className="text-white ml-1" fill="currentColor" />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3 bg-white/90 backdrop-blur-md rounded-sm px-4 py-3 z-10">
                  <Youtube size={20} className="text-[#003B5C]" />
                  <span className="text-sm font-semibold text-[#003B5C]">@garlandebenezerghanaiansda</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Live: embedded player
  return (
    <section data-testid="sermon-of-the-week-section" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-4">
              Sermon of the Week
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#003B5C] leading-tight">
              This week's message.
            </h2>
          </div>
          <Link
            to="/sermons"
            data-testid="sermon-week-watch-more"
            className="inline-flex items-center gap-2 bg-[#003B5C] text-white px-7 py-3.5 rounded-sm text-xs font-bold tracking-[0.18em] uppercase hover:bg-[#002A42] transition-all self-start sm:self-auto group"
          >
            Watch More Sermons
            <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div data-testid="sermon-week-player" className="lg:col-span-8 aspect-video bg-[#003B5C] rounded-sm overflow-hidden relative group">
            {play ? (
              <iframe
                title={video.title}
                src={`${video.embed_url}?autoplay=1`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <button
                onClick={() => setPlay(true)}
                data-testid="sermon-week-play"
                className="w-full h-full relative"
              >
                <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-[#D4A437] flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play size={32} className="text-white ml-1" fill="currentColor" />
                  </div>
                </div>
              </button>
            )}
          </div>
          <div className="lg:col-span-4 flex flex-col justify-center">
            <h3 className="font-display text-2xl lg:text-3xl font-bold text-[#003B5C] leading-tight mb-4">
              {video.title}
            </h3>
            {video.published && (
              <div className="flex items-center gap-2 text-sm text-[#1E1E1E]/65 mb-5">
                <Calendar size={14} className="text-[#D4A437]" /> {formatDate(video.published)}
              </div>
            )}
            {video.description && (
              <p className="text-sm leading-relaxed text-[#1E1E1E]/70 line-clamp-5 mb-6">
                {video.description}
              </p>
            )}
            <a
              href={video.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sermon-week-youtube-link"
              className="inline-flex items-center gap-2 text-sm text-[#003B5C] font-semibold border-b-2 border-[#D4A437] pb-0.5 self-start hover:gap-3 transition-all"
            >
              Watch on YouTube <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
